import { mergeClasses } from '@fluentui/react-components';
import type { IField } from '@moko/utils';
import { getFieldPanelContext } from '@moko/utils';
import { FieldItem } from './FieldItem';
import { useOtherInfoContext } from './OtherInfoContext';
import { getPanelColumns } from './panelLayout';
import { usePanelFieldsStyles } from './panelFieldsStyles';

interface FieldListProps {
  fields: IField[];
  parentName?: string;
}

const isRepeatingPanel = (field: IField) => {
  const mode = field.repeatingMode?.toString()?.trim();
  return Boolean(mode?.startsWith('M') || mode?.startsWith('E'));
};

export const FieldList = ({ fields, parentName = 'root' }: FieldListProps) => {
  const {
    params,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    unansweredOnly,
    getFieldCompletionIndicator,
  } = useOtherInfoContext();
  const styles = usePanelFieldsStyles();

  const isUnansweredVisible = (field: IField): boolean => {
    if (field.effect === 'Hide') return false;
    if (!unansweredOnly) return true;

    if (field.fieldType === 'Panel') {
      if (isRepeatingPanel(field)) return true;
      return fields.some(
        (child) => child.parentName === field.name && isUnansweredVisible(child)
      );
    }

    const indicator = getFieldCompletionIndicator(field.name);
    return indicator.isCompletable && !indicator.isComplete;
  };

  const filteredFields = fields.filter((field) => {
    if (parentName === 'root') {
      if (
        field.parentName !== null &&
        field.parentName !== undefined &&
        field.parentName !== 'root'
      ) {
        return false;
      }
    } else if (field.parentName !== parentName) {
      return false;
    }
    return isUnansweredVisible(field);
  });

  const parentPanel =
    parentName === 'root'
      ? undefined
      : params.metadata.fields.find((f) => f.name === parentName);
  const columns =
    parentName === 'root' ? 1 : getPanelColumns(parentPanel?.options);

  const fieldKey = (field: IField) => {
    const { fieldParentName, manualRepeating, entityRepeating } =
      getFieldPanelContext(field.name, params.metadata.fields);
    if (fieldParentName && manualRepeating) {
      return `${field.name}-${getRepeatingPanelIndex(fieldParentName)}`;
    }
    if (fieldParentName && entityRepeating) {
      return `${field.name}-e${currentElementInEntityRepeatingPanel}`;
    }
    return field.name;
  };

  return (
    <div className={styles.scroller}>
      <div
        className={mergeClasses(
          styles.grid,
          columns === 2 && styles.gridCols2,
          columns === 3 && styles.gridCols3
        )}
      >
        {filteredFields.flatMap((field) => {
          const before = params.renderBeforeField?.(field.name);
          const extra = params.renderAfterField?.(field.name);
          const cellClass = mergeClasses(
            styles.cell,
            field.fullWidth && styles.cellFullWidth
          );
          const slotClass = mergeClasses(
            styles.cell,
            parentName === 'orderBasic' && styles.cellFullWidth
          );
          return [
            before ? (
              <div key={`${fieldKey(field)}-before`} className={slotClass}>
                {before}
              </div>
            ) : null,
            <div key={fieldKey(field)} className={cellClass}>
              <FieldItem field={field} fields={fields} />
            </div>,
            extra ? (
              <div key={`${fieldKey(field)}-after`} className={slotClass}>
                {extra}
              </div>
            ) : null,
          ];
        })}
      </div>
    </div>
  );
};
